/**
 * WarehouseEditor — the multi-row warehouse-services form (type / units / days /
 * start / notes → C's warehouseServices[]). One shared, controlled, presentational
 * component used in BOTH places that collect warehouse RFQ detail (DRY):
 *   - the RFQ creator's WAREHOUSE section card (RfqSectionDetails), and
 *   - the booking confirmation's "Additional services" warehouse block
 *     (BookingRfqSections), so a warehouse RFQ raised from a booking carries the
 *     same full options a /rfq/new warehouse RFQ does (#77 booking-services parity).
 *
 * Each row is one warehouse service that can be quoted by a different vendor. The
 * parent owns the `{ services: [...] }` value and gets the next value via onChange.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Select, Input, Icon } from 'antd';
import { WAREHOUSE_SERVICE_TYPES, emptyWarehouseService } from '../../util/rfqSections';

const { Option } = Select;

const labelStyle = { display: 'block', fontSize: 12, color: '#888', marginBottom: 4 };
const fieldRow = { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 };
const addLinkStyle = {
  display: 'inline-block',
  marginTop: 8,
  color: '#1d4ed8',
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
};

function eventValue(e) {
  return e?.target ? e.target.value : e;
}

export default function WarehouseEditor({ value, onChange }) {
  const v = value || {};
  const services =
    Array.isArray(v.services) && v.services.length ? v.services : [emptyWarehouseService()];

  const setService = (i, field) => (e) => {
    const val = eventValue(e);
    onChange({
      services: services.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)),
    });
  };
  const addService = () => onChange({ services: [...services, emptyWarehouseService()] });
  const removeService = (i) =>
    onChange({ services: services.length > 1 ? services.filter((_, idx) => idx !== i) : services });

  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
        One row per warehouse service — each can be quoted by a different vendor.
      </div>
      {services.map((s, i) => (
        <div
          key={s._key != null ? s._key : i}
          style={{
            background: '#fff',
            border: '1px solid #eee',
            borderRadius: 4,
            padding: '8px 10px',
            marginTop: i === 0 ? 0 : 8,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Service #{i + 1}</span>
            {services.length > 1 && (
              <Icon
                type="close"
                title="Remove service"
                role="button"
                tabIndex={0}
                aria-label={`Remove service #${i + 1}`}
                style={{ color: '#bbb', cursor: 'pointer' }}
                onClick={() => removeService(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    removeService(i);
                  }
                }}
              />
            )}
          </div>
          <div style={fieldRow}>
            <div style={{ flex: '0 0 150px' }}>
              <label style={labelStyle}>Service type</label>
              <Select
                style={{ width: '100%' }}
                value={s.serviceType}
                onChange={setService(i, 'serviceType')}
              >
                {WAREHOUSE_SERVICE_TYPES.map((t) => (
                  <Option key={t} value={t}>
                    {t}
                  </Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: '0 0 90px' }}>
              <label style={labelStyle}>Units / pallets</label>
              <Input type="number" min={0} value={s.units} onChange={setService(i, 'units')} />
            </div>
            <div style={{ flex: '0 0 80px' }}>
              <label style={labelStyle}>Days</label>
              <Input type="number" min={0} value={s.days} onChange={setService(i, 'days')} />
            </div>
            <div style={{ flex: '0 0 140px' }}>
              <label style={labelStyle}>Start date</label>
              <Input type="date" value={s.startDate} onChange={setService(i, 'startDate')} />
            </div>
          </div>
          <div style={fieldRow}>
            <div style={{ flex: '1 1 100%' }}>
              <label style={labelStyle}>Handling notes</label>
              <Input
                value={s.notes || ''}
                onChange={setService(i, 'notes')}
                placeholder="e.g. Rack storage, climate-OK"
              />
            </div>
          </div>
        </div>
      ))}
      <span
        style={addLinkStyle}
        role="button"
        tabIndex={0}
        onClick={addService}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            addService();
          }
        }}
      >
        + add warehouse service
      </span>
    </div>
  );
}

WarehouseEditor.propTypes = {
  /** Controlled value: { services: [{ serviceType, units, days, startDate, notes }] }. */
  value: PropTypes.object,
  /** Called with the next value object on any change. */
  onChange: PropTypes.func.isRequired,
};
